import { Button, Form, Space } from 'antd'
import { Create } from '@refinedev/antd'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { useProductImageUpload } from '../../shared/hooks/useProductImageUpload'
import { useCreateProduct } from './useCreateProduct'
import { ProductCreateForm } from './ProductCreateForm'
import type { ProductCreateFormValues } from './ProductCreateFormTypes'
import { confirmPopup } from '@/components/popup'

export function ProductCreate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm<ProductCreateFormValues>()
  const { upload, isUploading } = useProductImageUpload()

  const handleSuccess = () => {
    void navigate('/products')
  }

  const { createProduct, isPending } = useCreateProduct(handleSuccess)

  const handleSubmit = () => {
    void form.validateFields().then((values) => {
      createProduct({
        name: values.name,
        description: values.description ?? null,
        productCategoryIds: values.productCategoryIds,
        price: { amount: values.price, currency: 'KRW' },
        shippingFee: { amount: values.shippingFee, currency: 'KRW' },
        quantity: values.quantity,
        images: values.images,
        salesChannel: values.salesChannel,
      })
    })
  }

  const handleCancel = () => {
    if (!form.isFieldsTouched()) {
      void navigate('/products')
      return
    }
    confirmPopup({
      title: t('common.modals.createCancelTitle'),
      content: t('common.modals.createCancelMessage'),
      okText: t('common.buttons.confirm'),
      cancelText: t('common.buttons.cancel'),
      okButtonProps: { 'data-testid': 'product-create-discard-confirm-btn' },
      cancelButtonProps: { 'data-testid': 'product-create-discard-cancel-btn' },
      onOk: () => void navigate('/products'),
    })
  }

  const headerButtons = (
    <Space>
      <Button data-testid="product-create-cancel-btn" onClick={handleCancel}>
        {t('common.buttons.cancel')}
      </Button>
      <Button
        data-testid="product-create-save-btn"
        type="primary"
        onClick={handleSubmit}
        loading={isPending || isUploading}
      >
        {t('common.buttons.save')}
      </Button>
    </Space>
  )

  return (
    <Create
      title={t('pages.products.createTitle')}
      headerButtons={headerButtons}
      saveButtonProps={{ style: { display: 'none' } }}
    >
      <ProductCreateForm form={form} onImageUpload={upload} isUploading={isUploading} />
    </Create>
  )
}
